<script setup lang="ts">
import { computed, nextTick, onMounted, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { Leaf, Moon, Sun } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import AppSidebar from '@/components/AppSidebar.vue'
import GameIcon from '@/components/GameIcon.vue'
import OcrStartupGate from '@/components/OcrStartupGate.vue'
import { preloadPrimaryRouteViews } from '@/router'
import { useAppStore } from '@/stores/app'
import { useLiveStore } from '@/stores/live'
import { GAME_ICONS } from '@/ui/game-icons'
import { initializeRankSync } from '@/services/rank-api'

const { t } = useI18n()
const app = useAppStore()
const live = useLiveStore()
const route = useRoute()
const isAdmin = computed(() => route.meta.layout === 'admin')
let publicAppInitialized = false

function initializeAfterFirstPaint(): void {
  if (publicAppInitialized || isAdmin.value) return
  publicAppInitialized = true
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      initializeRankSync()
      void live.initialize()

      const preloadOtherRoutes = () => {
        void preloadPrimaryRouteViews(typeof route.name === 'string' ? route.name : undefined)
      }
      if ('requestIdleCallback' in window) window.requestIdleCallback(preloadOtherRoutes, { timeout: 1_500 })
      else globalThis.setTimeout(preloadOtherRoutes, 0)
    })
  })
}

onMounted(initializeAfterFirstPaint)
watch(isAdmin, (admin) => {
  if (!admin) initializeAfterFirstPaint()
})
watch(() => route.fullPath, async () => {
  await nextTick()
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true })
})
</script>

<template>
  <main v-if="isAdmin" id="main-content" class="admin-route-main" tabindex="-1">
    <RouterView />
  </main>
  <template v-else>
    <a class="skip-link" href="#main-content">{{ t('app.skipToContent') }}</a>
    <OcrStartupGate />
    <div class="app-shell">
    <AppSidebar />
    <header class="mobile-topbar">
      <RouterLink class="mobile-brand" to="/" :aria-label="t('app.name')">
        <span class="brand-mark"><GameIcon :src="GAME_ICONS.mapleLeaf" dimension="1.75rem" eager><Leaf stroke-width="2.2" /></GameIcon></span>
        <span>{{ t('app.name') }}</span>
      </RouterLink>
      <button class="icon-btn topbar-theme" type="button" :title="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" :aria-label="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" @click="app.toggleTheme">
        <Moon v-if="app.theme === 'light'" style="width:1.125rem;height:1.125rem" aria-hidden="true" />
        <Sun v-else style="width:1.125rem;height:1.125rem" aria-hidden="true" />
      </button>
    </header>
    <main id="main-content" class="app-main" tabindex="-1">
      <RouterView v-slot="{ Component }">
        <Transition name="page" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>
    </div>
  </template>
</template>
